import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  MatButtonModule,
  MatDatepickerModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatIconModule,
  MatInputModule,
  MatMenuModule,
  MatNativeDateModule,
  MatSelectModule
} from "@angular/material";
import { MatDialogModule } from "@angular/material/dialog";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router } from "@angular/router";
import Amplify from "@aws-amplify/auth";
import { BroadcastService, MsalAngularConfiguration, MsalGuard, MsalModule, MsalService, MSAL_CONFIG, MSAL_CONFIG_ANGULAR } from "@azure/msal-angular";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive";
import { AgGridModule } from "ag-grid-angular";
import "ag-grid-enterprise";
import { AngularFontAwesomeModule } from "angular-font-awesome";
import { AmplifyAngularModule, AmplifyService } from "aws-amplify-angular";
import { Configuration } from "msal";
import { SatDatepickerModule, SatNativeDateModule } from "saturn-datepicker";
import { AppRoutingModule } from "./app-routing.module";
import {
  AgreedToTerms, AuthGuard,
  MustBeDistributor,
  UserCanAdmin
} from "./auth.guard";
import { AdminToolsComponent } from "./components/admin-tools/admin-tools.component";
import { AppComponent } from "./components/app.component";
import { DateRangePickerCompponent } from "./components/common/date-range-picker.component";
import { HomeComponent } from "./components/home/home.component";
import { IdleMessageComponent } from "./components/idle-message/idle-message.component";
import { IFrameComponent } from "./components/iframe.component";
import { PageLoginCognitoComponent } from "./components/page-login-cognito/page-login-cognito.component";
import { PageLoginComponent } from "./components/page-login/page-login.component";
import { PortalShellComponent } from "./components/portal-shell/portal-shell.component";
import { ProfileModalUserDetailComponent } from "./components/profile-modal-user-detail/profile-modal-user-detail.component";
import { ProfileShellProfileTabComponent } from "./components/profile-shell-profile-tab/profile-shell-profile-tab.component";
import { ProfileShellUsersTabComponent } from "./components/profile-shell-users-tab/profile-shell-users-tab.component";
import { ProfileShellComponent } from "./components/profile-shell/profile-shell.component";
import { TermsAndConditionsComponent } from "./components/terms-and-conditions/terms-and-conditions.component";
import { UserManagementComponent } from "./components/user-management/user-management.component";
import { MyInterceptor } from "./httpinterceptor";
import { PageBusinessIntelligenceComponent } from "./pages/business-intelligence";
import { PageCommunicationsComponent } from "./pages/communications";
import { PageCompaniesComponent } from "./pages/companies";
import { PageCustomerProfileComponent } from "./pages/customerProfile";
import { DigitizationRootComponent } from "./pages/digitization";
import { PageIncashComponent } from "./pages/incash";
import { PageRecsComponent } from "./pages/inmatch";
import { PageInvoiceComponent } from "./pages/invoices";
import { LandingPageComponent } from "./pages/landing";
import { PageOrdersComponent } from "./pages/orders";
import { PageRelationshipsComponent } from "./pages/realationships";
import { PageUserPermissionsComponent } from "./pages/userPermissions";
import {
  ButtonRendererComponent,
  ConfirmationDialog
} from "./renderers/button-renderer.component";
import { IconRendererComponent } from "./renderers/icon-renderer.component";
import { ActivityMonitoringService } from "./services/activity-monitoring.services";
import { AppConfigService, AuthConfig } from "./services/app-config.service";
import { AuthenticationFactory } from "./services/auth-factory";
import { AuthService } from "./services/auth.service";
import { UserResolver } from "./services/user-resolver";

const appInitializerFn = (appConfig: AppConfigService) => {
  return () => {
    return appConfig.loadAppConfig().then(() => {
      if (!appConfig.isB2cEnabled()) {
        const config = appConfig.getConfig();
        Amplify.configure({
          ...config.cognito.amplifyParams,
          Auth: { oauth: { ...config.cognito.oauth } }
        });
      }
    });
  };
};

export function getConfig(appConfig: AppConfigService): Configuration {
  let authConfig = appConfig.getConfig().msalAuth;
  return {
    auth: authConfig,
    cache: { cacheLocation: 'sessionStorage' },
    system: {
      tokenRenewalOffsetSeconds: getTokenRenewalOffsetSeconds(authConfig)
    }
  }
};

function getTokenRenewalOffsetSeconds(authConfig: AuthConfig) {
  // returns the amount of time before a token expires that acquireTokenSilent should go
  // ahead and refresh the token (as opposed to grabbing it from the cache).
  // Once we replace getTokenFromCache() (or whatever the API gateways use)
  // with a call to the authService, we can set this to a reasonable value like 2 minutes.
  // But since right now we manually tell MSAL to retrieve the  token  every X seconds,
  // we return a duration long enough here so that MSAL will get a new token (with a new expiration date)
  // instead of keeping the existing one from the cache
  const tokenLifetime = 60 * 30; // inappropriate knowledge that the token has a one hour expiration
  return tokenLifetime - (authConfig.timeouts.tokenRefreshSeconds - 60); // i.e. one minute before we 'refresh' the token
}

export function getAngularConfig(): MsalAngularConfiguration {
  return {
    popUp: false,
    extraQueryParameters: { platformcodes: 'lqx' },
    consentScopes: [
      'openid',
      'profile',
    ],

  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    UserManagementComponent,
    PageOrdersComponent,
    PageRecsComponent,
    LandingPageComponent,
    PageCommunicationsComponent,
    PageRelationshipsComponent,
    PageCompaniesComponent,
    PageUserPermissionsComponent,
    PageLoginComponent,
    PortalShellComponent,
    ProfileShellComponent,
    IFrameComponent,
    ProfileShellProfileTabComponent,
    ProfileShellUsersTabComponent,
    ProfileModalUserDetailComponent,
    AdminToolsComponent,
    TermsAndConditionsComponent,
    PageLoginCognitoComponent,
    ButtonRendererComponent,
    ConfirmationDialog,
    IconRendererComponent,
    PageInvoiceComponent,
    PageIncashComponent,
    DigitizationRootComponent,
    PageCustomerProfileComponent,
    PageBusinessIntelligenceComponent,
    IdleMessageComponent,
    DateRangePickerCompponent
  ],

  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AgGridModule.withComponents([
      ButtonRendererComponent,
      ConfirmationDialog,
      IconRendererComponent
    ]),
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AngularFontAwesomeModule,
    AmplifyAngularModule,
    NgIdleKeepaliveModule.forRoot(),
    NgbModule,
    AppRoutingModule,
    MatExpansionModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    MsalModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    SatDatepickerModule,
    SatNativeDateModule 
  ],
  providers: [
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [AppConfigService]
    },
    { provide: HTTP_INTERCEPTORS, useClass: MyInterceptor, multi: true },
    {
      provide: AuthService,
      useFactory: AuthenticationFactory,
      deps: [AppConfigService, MsalService, MsalGuard, Router, BroadcastService]
    },
    AuthGuard,
    AmplifyService,
    MsalService,
    {
      provide: MSAL_CONFIG,
      useFactory: getConfig,
      deps: [AppConfigService]
    },
    {
      provide: MSAL_CONFIG_ANGULAR,
      useFactory: getAngularConfig,
    },
    MustBeDistributor,
    UserCanAdmin,
    UserResolver,
    AgreedToTerms,
    ActivityMonitoringService
  ],
  bootstrap: [AppComponent],
  entryComponents: [IdleMessageComponent]
})
export class AppModule { }
